import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PlanetaryMixer = ({ selectedPlanets, onPlanetToggle, onIntensityChange }) => {
  const [expandedPlanet, setExpandedPlanet] = useState(null);

  const planets = [
    { 
      name: 'Sun', 
      symbol: '☉', 
      color: '#F59E0B', 
      icon: 'Sun',
      intensity: 85,
      frequency: '261.63 Hz',
      note: 'C',
      isMuted: false,
      effects: ['Reverb', 'Chorus']
    },
    { 
      name: 'Moon', 
      symbol: '☽', 
      color: '#8B5CF6', 
      icon: 'Moon',
      intensity: 70,
      frequency: '293.66 Hz',
      note: 'D',
      isMuted: false,
      effects: ['Echo', 'Tremolo']
    },
    { 
      name: 'Mercury', 
      symbol: '☿', 
      color: '#06B6D4', 
      icon: 'Zap',
      intensity: 60,
      frequency: '329.63 Hz',
      note: 'E',
      isMuted: true,
      effects: ['Distortion']
    },
    { 
      name: 'Venus', 
      symbol: '♀', 
      color: '#EC4899', 
      icon: 'Heart',
      intensity: 90,
      frequency: '349.23 Hz',
      note: 'F',
      isMuted: false,
      effects: ['Harmony', 'Sustain']
    },
    { 
      name: 'Mars', 
      symbol: '♂', 
      color: '#EF4444', 
      icon: 'Flame',
      intensity: 75,
      frequency: '392.00 Hz',
      note: 'G',
      isMuted: false,
      effects: ['Drive', 'Compression']
    },
    { 
      name: 'Jupiter', 
      symbol: '♃', 
      color: '#10B981', 
      icon: 'Crown',
      intensity: 80,
      frequency: '440.00 Hz',
      note: 'A',
      isMuted: false,
      effects: ['Expansion', 'Warmth']
    },
    { 
      name: 'Saturn', 
      symbol: '♄', 
      color: '#6B7280', 
      icon: 'Shield',
      intensity: 45,
      frequency: '493.88 Hz',
      note: 'B',
      isMuted: false,
      effects: ['Limiter', 'Gate']
    },
    { 
      name: 'Uranus', 
      symbol: '♅', 
      color: '#3B82F6', 
      icon: 'Sparkles',
      intensity: 30,
      frequency: '277.18 Hz',
      note: 'C#',
      isMuted: true,
      effects: ['Modulation']
    },
    { 
      name: 'Neptune', 
      symbol: '♆', 
      color: '#8B5CF6', 
      icon: 'Waves',
      intensity: 55,
      frequency: '311.13 Hz',
      note: 'D#',
      isMuted: false,
      effects: ['Ambient', 'Delay']
    },
    { 
      name: 'Pluto', 
      symbol: '♇', 
      color: '#7C3AED', 
      icon: 'Circle',
      intensity: 25,
      frequency: '369.99 Hz',
      note: 'F#',
      isMuted: true,
      effects: ['Transform']
    }
  ];

  const [planetStates, setPlanetStates] = useState(planets);

  const handleIntensityChange = (planetName, newIntensity) => {
    setPlanetStates(prev => 
      prev.map(planet => 
        planet.name === planetName 
          ? { ...planet, intensity: newIntensity }
          : planet
      )
    );
    onIntensityChange(planetName, newIntensity);
  };

  const handleMuteToggle = (planetName) => {
    setPlanetStates(prev => 
      prev.map(planet => 
        planet.name === planetName 
          ? { ...planet, isMuted: !planet.isMuted }
          : planet
      )
    );
    onPlanetToggle(planetName);
  };

  const handleSoloToggle = (planetName) => {
    setPlanetStates(prev => 
      prev.map(planet => ({
        ...planet,
        isMuted: planet.name !== planetName
      }))
    );
  };

  const toggleExpanded = (planetName) => {
    setExpandedPlanet(expandedPlanet === planetName ? null : planetName);
  };

  const activePlanets = planetStates.filter(planet => !planet.isMuted);
  const totalIntensity = activePlanets.reduce((sum, planet) => sum + planet.intensity, 0);
  const averageIntensity = activePlanets.length > 0 ? Math.round(totalIntensity / activePlanets.length) : 0;

  return (
    <div className="w-full glass bg-surface/95 backdrop-blur-cosmic rounded-xl border border-border">
      {/* Header */}
      <div className="p-4 border-b border-border/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center">
              <Icon name="Sliders" size={16} color="white" />
            </div>
            <div>
              <h3 className="text-lg font-heading font-semibold text-text-primary">
                Planetary Mixer
              </h3>
              <p className="text-xs text-text-secondary font-mono">
                {activePlanets.length} active • {averageIntensity}% avg intensity
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              iconName="RotateCcw"
              onClick={() => console.log('Reset mixer')}
              className="w-8 h-8 p-0"
            />
            <Button
              variant="ghost"
              iconName="Save"
              onClick={() => console.log('Save preset')}
              className="w-8 h-8 p-0"
            />
          </div>
        </div>
      </div>

      {/* Master Controls */}
      <div className="p-4 border-b border-border/50">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-text-primary">Master Volume</span>
              <span className="text-xs text-text-secondary font-mono">100%</span>
            </div>
            <input
              type="range"
              min="0"
              max="100"
              defaultValue="100"
              className="w-full h-2 bg-surface rounded-full appearance-none slider cursor-pointer"
            />
          </div>
          <Button
            variant="ghost"
            iconName="Volume2"
            className="w-10 h-10 p-0"
          />
        </div>
      </div>

      {/* Planet Channels */}
      <div className="max-h-96 overflow-y-auto">
        {planetStates.map((planet) => (
          <div key={planet.name} className="border-b border-border/30 last:border-b-0">
            {/* Main Channel Strip */}
            <div className="p-4">
              <div className="flex items-center space-x-4">
                {/* Planet Info */}
                <div className="flex items-center space-x-3 w-32">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-mono"
                    style={{ backgroundColor: planet.color }}
                  >
                    {planet.symbol}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-text-primary">
                      {planet.name}
                    </div>
                    <div className="text-xs text-text-secondary font-mono">
                      {planet.note} • {planet.frequency}
                    </div>
                  </div>
                </div>

                {/* Intensity Slider */}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-text-secondary">Intensity</span>
                    <span className="text-xs text-text-secondary font-mono">
                      {planet.intensity}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={planet.intensity}
                    onChange={(e) => handleIntensityChange(planet.name, parseInt(e.target.value))}
                    className="w-full h-1 bg-surface rounded-full appearance-none slider cursor-pointer"
                    disabled={planet.isMuted}
                  />
                </div>

                {/* Controls */}
                <div className="flex items-center space-x-2">
                  <Button
                    variant={planet.isMuted ? "ghost" : "primary"}
                    iconName={planet.isMuted ? "VolumeX" : "Volume2"}
                    onClick={() => handleMuteToggle(planet.name)}
                    className="w-8 h-8 p-0"
                  />
                  <Button
                    variant="ghost"
                    iconName="Headphones"
                    onClick={() => handleSoloToggle(planet.name)}
                    className="w-8 h-8 p-0"
                  />
                  <Button
                    variant="ghost"
                    iconName={expandedPlanet === planet.name ? "ChevronUp" : "ChevronDown"}
                    onClick={() => toggleExpanded(planet.name)}
                    className="w-8 h-8 p-0"
                  />
                </div>
              </div>

              {/* Expanded Controls */}
              {expandedPlanet === planet.name && (
                <div className="mt-4 pt-4 border-t border-border/30">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Effects */}
                    <div>
                      <h4 className="text-sm font-medium text-text-primary mb-2">Effects</h4>
                      <div className="space-y-2">
                        {planet.effects.map((effect) => (
                          <div key={effect} className="flex items-center justify-between">
                            <span className="text-xs text-text-secondary">{effect}</span>
                            <input
                              type="range"
                              min="0"
                              max="100"
                              defaultValue="50"
                              className="w-20 h-1 bg-surface rounded-full appearance-none slider cursor-pointer"
                            />
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* EQ */}
                    <div>
                      <h4 className="text-sm font-medium text-text-primary mb-2">EQ</h4>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-text-secondary">High</span>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            defaultValue="50"
                            className="w-20 h-1 bg-surface rounded-full appearance-none slider cursor-pointer"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-text-secondary">Mid</span>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            defaultValue="50"
                            className="w-20 h-1 bg-surface rounded-full appearance-none slider cursor-pointer"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-text-secondary">Low</span>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            defaultValue="50"
                            className="w-20 h-1 bg-surface rounded-full appearance-none slider cursor-pointer"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #4A5FBF;
          cursor: pointer;
        }
        .slider::-moz-range-thumb {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: #4A5FBF;
          cursor: pointer;
          border: none;
        }
      `}</style>
    </div>
  );
};

export default PlanetaryMixer;